import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/modern/components/ui/select";
import { Textarea } from "@/modern/components/ui/textarea";
import { Card } from "@/modern/components/ui/card";
import { cn } from "@/modern/utils";
import { IconSelector } from "../components/IconSelector";
import { ModalColorPicker } from "../components/ModalColorPicker";
import { VIEW_STATE_PIITS_UPDATE_LISTENER, ViewFolderTypes } from "@/other/constants/OtherKeys.jsx";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { Settings } from "lucide-react";
import { useViewEditor } from "../context/ViewEditorContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

const FormField = ({
  label,
  children,
  className
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("space-y-2", className)}>
    <Label className="text-sm font-medium text-foreground">{label}</Label>
    <div className="w-full">{children}</div>
  </div>
);

const ManageView = () => {
  const [swivuSaveButtonStateListener, setSwivuSaveButtonStateListener] = useState<(() => void) | null>(null)
  const context = useSwivuBryntumContext();
  const { record, isSaveAsView = false, form, formState, focusViewNameRef } = useViewEditor();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [currentSelectedColumnInCombo, setCurrentSelectedColumnInCombo] = useState<number | null>(record.folderId);
  const [folders, setFolders] = useState<Array<{ id: number; name: string }>>([]);
  const [icons, setIcons] = useState<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any

  const folderId = form.watch("folderId");
  const color = form.watch("color");
  const iconId = form.watch("iconId");

  useEffect(() => {
    // Load folders
    // @ts-expect-error typescript does not work as expected with bryntum
    const folderData = context.project.viewsFolderStore.query((folder: any) => ({
      id: folder.id,
      name: folder.name
    }), true);
    // @ts-expect-error typescript does not work as expected with bryntum
    setFolders(folderData);

    // Load icons
    const iconData = context.project.resourceTagValues.query(
      (icon: any) => icon.tagSet === "Icon", // eslint-disable-line @typescript-eslint/no-explicit-any
      true
    );
    setIcons(iconData);

    // If save as view, set to More folder
    if (isSaveAsView) {
      const moreFolder = context.project.viewsFolderStore.find((folder: any) => folder.type === ViewFolderTypes.more); // eslint-disable-line @typescript-eslint/no-explicit-any
      if (moreFolder) {
        form.setValue("folderId", moreFolder.id);
        setCurrentSelectedColumnInCombo(moreFolder.id);
      }
    }
  }, [context, isSaveAsView]);

  useEffect(() => {
    focusViewNameRef.current = () => nameInputRef.current?.focus();
    return () => {
      focusViewNameRef.current = null;
    };
  }, [focusViewNameRef]);

  useEffect(() => {
    if (folderId) {
      updateColumnManagerData(folderId);
    }
  }, [folderId]);

  // Dynamic color update - update icon color when color changes
  useEffect(() => {
    if (color) {
      // Update icon color if icon is selected
      const iconElement = document.querySelector('.icon-selector-icon');
      if (iconElement) {
        (iconElement as HTMLElement).style.color = color;
      }
    }
  }, [color, iconId]);

  // Focus management - focus close button to prevent accidental delete
  useEffect(() => {
    if (!isSaveAsView) {
      const timer = setTimeout(() => {
        const closeButton = document.querySelector('[aria-label="Close"]') as HTMLElement;
        if (closeButton) {
          closeButton.focus();
        }
      }, 200);
      return () => clearTimeout(timer);
    }
    return;
  }, [isSaveAsView]);

  const updateColumnManagerData = (folderId: number) => {
    // Track current selected column for the column manager display
    setCurrentSelectedColumnInCombo(folderId);
  };

  const onColumnManagerSettingsClick = () => {
    // @ts-expect-error typescript does not work as expected with bryntum
    const folderRecord = context.project.viewsFolderStore.getById(currentSelectedColumnInCombo);
    context.editorHandler.openEditor(
      EDITOR_KEYS.viewOrganizerEditor,
      // @ts-expect-error typescript does not work as expected with bryntum
      null,
      { folderRecord },
      {
        useExisting: true,
        by: "recordType"
      }
    );
  };

  useEffect(() => {
    if (record.isOwnView && !swivuSaveButtonStateListener) {
      const listener = context.project.userPreferencesStore.on(VIEW_STATE_PIITS_UPDATE_LISTENER, () => {
        // Force re-render
        form.trigger();
      });
      setSwivuSaveButtonStateListener(() => listener);
    }

    return () => {
      if (swivuSaveButtonStateListener) {
        swivuSaveButtonStateListener();
      }
    };
  }, [record, context, swivuSaveButtonStateListener]);

  const folderName = folders.find((f) => f.id === currentSelectedColumnInCombo)?.name || "";

  return (
    <div className="p-4">

      <div className="space-y-4">
        {/* Basic Information Section */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Basic Information</h3>
              <p className="text-xs text-muted-foreground">Name and description for this view</p>
            </div>

            <div className="space-y-4">
              <FormField label="View Name">
        <Controller
          control={form.control}
          name="name"
          render={({ field }) => (
            <div className="space-y-1">
              <Input
                {...field}
                ref={(el) => {
                  field.ref(el);
                  (nameInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                }}
                autoFocus={isSaveAsView}
                disabled={!record.isOwnView}
                style={{ color: color || "inherit" }}
                placeholder="Enter view name"
              />
              {formState.errors.name && (
                <p className="text-xs text-destructive">{String(formState.errors.name.message)}</p>
              )}
              {isSaveAsView && field.value === record.name && (
                <p className="text-xs text-destructive">
                  Name of view must not match with the one being saved from
                </p>
              )}
            </div>
          )}
        />
      </FormField>

      <FormField label="Description">
        <Controller
          control={form.control}
          name="viewDescription"
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ""}
              disabled={!record.isOwnView}
              className="min-h-[70px] resize-none"
              placeholder="Add a description for this view..."
            />
          )}
        />
      </FormField>
            </div>
          </div>
        </Card>

        {/* Organization Section */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Organization</h3>
              <p className="text-xs text-muted-foreground">Column placement and management</p>
            </div>

            <div className="space-y-4">
              <FormField label="Column">
        <Controller
          control={form.control}
          name="folderId"
          render={({ field }) => (
            <Select
              value={field.value?.toString() || ""}
              onValueChange={(val) => field.onChange(parseInt(val))}
              disabled={!record.isOwnView}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a column" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5} className={"z-[--swivu-bryntum-z-index]"}>
                {folders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id.toString()}>
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormField>

      <FormField label="Column Manager">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-swivu-input flex-1 justify-start font-normal"
            disabled
          >
            <span className="text-muted-foreground">{folderName || "See Column Views"}</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-swivu-input w-swivu-input shrink-0"
            onClick={onColumnManagerSettingsClick}
            title="Manage Column Views"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </FormField>
            </div>
          </div>
        </Card>

        {/* Appearance Section */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Appearance</h3>
              <p className="text-xs text-muted-foreground">Customize icon and color</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Icon">
          <Controller
            control={form.control}
            name="iconId"
            render={({ field }) => (
              <IconSelector
                value={field.value}
                onChange={field.onChange}
                items={icons}
                disabled={false}
                color={color}
              />
            )}
          />
        </FormField>

        <FormField label="Color">
          <Controller
            control={form.control}
            name="color"
            render={({ field }) => (
              <ModalColorPicker
                value={field.value}
                onChange={field.onChange}
                clearable={true}
              />
            )}
          />
        </FormField>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ManageView;
